import React, { createContext, FC, JSX, useEffect, useRef, useState } from "react";
import {
  AdequacyBreakdownData, AdequacyBreakdownDrillInModel, ColorSet, CompositeFillableGapCountDrillInModel, CompositeFillableGapsData,
  CountyStatusData, DisplayFilters, EcdApiFilters, EcdReportGenerationWizardContextSteps, ExceptionsData, ExceptionsDrillInModel, GapOverviewData, GapsByAgeData, LineOfBusinessData,
  TimeFrameOptions,
  TopCountiesByGapData, TopSpecialtiesByGapData
} from "../interfaces";
import moment from "moment";
import { EcdReportGenerationRouteState } from "../EcdReportGenerationRouteState";
import { useLocation } from "react-router-dom";
import EnterpriseComplianceDashboardService from "../enterpriseComplianceDashboardService";
import html2canvas from "html2canvas";

interface EcdGenerateReportContextProps {
  addGraphDetails: boolean;
  adequacyBreakdownData: AdequacyBreakdownData;
  adequacyBreakdownDrillInData: AdequacyBreakdownDrillInModel[];
  compositeFillableGapsData: CompositeFillableGapsData;
  compositeFillableGapsDrillInData: CompositeFillableGapCountDrillInModel[];
  countyStatusData: CountyStatusData[];
  displayFilters: DisplayFilters;
  error: string | JSX.Element;
  exceptionsData: ExceptionsData[];
  exceptionsDrillInData: ExceptionsDrillInModel[];
  exceptionsTimeFrame: TimeFrameOptions;
  fileName: string;
  gapOverviewData: GapOverviewData[];
  gapOverviewTimeFrame: TimeFrameOptions;
  gapsByAgeData: GapsByAgeData[];
  gapsByAgeTimeFrame: TimeFrameOptions;
  gapsBySpecialtyData: TopSpecialtiesByGapData[];
  includeAdequacyBreakdown: boolean;
  includeCompositeFillableGaps: boolean;
  includeCountyStatus: boolean;
  includeExceptions: boolean;
  includeFilterPage: boolean;
  includeGapOverview: boolean;
  includeGapsByAge: boolean;
  includeLineOfBusiness: boolean;
  includeTopCounties: boolean;
  includeTopSpecialties: boolean;
  lineOfBusinessData: LineOfBusinessData[];
  loading: boolean;
  page: EcdReportGenerationWizardContextSteps;
  setAddGraphDetails: React.Dispatch<React.SetStateAction<boolean>>;
  setFileName: React.Dispatch<React.SetStateAction<string>>;
  setIncludeAdequacyBreakdown: React.Dispatch<React.SetStateAction<boolean>>;
  setIncludeCompositeFillableGaps: React.Dispatch<React.SetStateAction<boolean>>
  setIncludeCountyStatus: React.Dispatch<React.SetStateAction<boolean>>
  setIncludeExceptions: React.Dispatch<React.SetStateAction<boolean>>
  setIncludeFilterPage: React.Dispatch<React.SetStateAction<boolean>>
  setIncludeGapOverview: React.Dispatch<React.SetStateAction<boolean>>
  setIncludeGapsByAge: React.Dispatch<React.SetStateAction<boolean>>
  setIncludeLineOfBusiness: React.Dispatch<React.SetStateAction<boolean>>
  setIncludeTopCounties: React.Dispatch<React.SetStateAction<boolean>>
  setIncludeTopSpecialties: React.Dispatch<React.SetStateAction<boolean>>
  setPage: React.Dispatch<React.SetStateAction<EcdReportGenerationWizardContextSteps>>;
  topCountiesByGapData: TopCountiesByGapData[];

  arrowPositive: string;
  arrowNegative: string;
  defaultColors: ColorSet;
  exceptionsArray: string[];
  exceptionsPrimary: string;
  fillableGapsPrimary: string;
  fillableGapsSecondary: string;
  gapsArray: string[];
  gapsPrimary: string;
  unfillableGaps: string;

  getHtmlCanvasElement: (selector: string, include: boolean) => Promise<HTMLCanvasElement>;
}

export const EcdGenerateReportContext = createContext<Partial<EcdGenerateReportContextProps>>({});

const EcdGenerateReportContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const routeState = useLocation<EcdReportGenerationRouteState>().state;
  const now = moment();
  const enterpriseComplianceDashboardService = useRef(new EnterpriseComplianceDashboardService()).current;

  //Wizard steps
  const [page, setPage] = useState<EcdReportGenerationWizardContextSteps>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | JSX.Element>("");


  //Step One: Export Details
  const [addGraphDetails, setAddGraphDetails] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>(`EnterpriseComplianceDashboardExport_${now.format("YYYYMMDD[_]HHmmss")}`);
  const [includeAdequacyBreakdown, setIncludeAdequacyBreakdown] = useState<boolean>(false);
  const [includeGapOverview, setIncludeGapOverview] = useState<boolean>(false);
  const [includeCompositeFillableGaps, setIncludeCompositeFillableGaps] = useState<boolean>(false);
  const [includeGapsByAge, setIncludeGapsByAge] = useState<boolean>(false);
  const [includeExceptions, setIncludeExceptions] = useState<boolean>(false);
  const [includeTopSpecialties, setIncludeTopSpecialties] = useState<boolean>(false);
  const [includeTopCounties, setIncludeTopCounties] = useState<boolean>(false);
  const [includeLineOfBusiness, setIncludeLineOfBusiness] = useState<boolean>(false);
  const [includeCountyStatus, setIncludeCountyStatus] = useState<boolean>(false);
  const [includeFilterPage, setIncludeFilterPage] = useState<boolean>(true);

  //Step Two: Export Preview Data
  const [adequacyBreakdownData] = useState<AdequacyBreakdownData>(routeState.adequacyBreakdownData);
  const [compositeFillableGapsData] = useState<CompositeFillableGapsData>(routeState.compositeFillableGapsData);
  const [topCountiesByGapData] = useState<TopCountiesByGapData[]>(routeState.topCountiesByGapData);
  const [exceptionsData] = useState<ExceptionsData[]>(routeState.exceptionsData);
  const [gapOverviewData] = useState<GapOverviewData[]>(routeState.gapOverviewData);
  const [gapsByAgeData] = useState<GapsByAgeData[]>(routeState.gapsByAgeData);
  const [gapsBySpecialtyData] = useState<TopSpecialtiesByGapData[]>(routeState.gapsBySpecialtyData);
  const [lineOfBusinessData] = useState<LineOfBusinessData[]>(routeState.lineOfBusinessData);
  const [countyStatusData] = useState<CountyStatusData[]>(routeState.countyStatusData);
  const [apiReadyFilters] = useState<EcdApiFilters>(routeState.apiReadyFilters);
  const [adequacyBreakdownDrillInData, setAdequacyBreakdownDrillInData] = useState<AdequacyBreakdownDrillInModel[]>(null);
  const [exceptionsDrillInData, setExceptionsDrillInData] = useState<ExceptionsDrillInModel[]>(null);
  const [compositeFillableGapsDrillInData, setcompositeFillableGapsDrillInData] = useState<CompositeFillableGapCountDrillInModel[]>(null);
  const [exceptionsTimeFrame] = useState<TimeFrameOptions>(routeState.exceptionsTimeFrame);
  const [gapOverviewTimeFrame] = useState<TimeFrameOptions>(routeState.gapOverviewTimeFrame);
  const [gapsByAgeTimeFrame] = useState<TimeFrameOptions>(routeState.gapsByAgeTimeFrame);
  const [displayFilters] = useState<DisplayFilters>(routeState.displayFilters);

  //Colors Context
  const [arrowPositive] = useState<string>(routeState.arrowPositive);
  const [arrowNegative] = useState<string>(routeState.arrowNegative);
  const [defaultColors] = useState<ColorSet>(routeState.defaultColors);
  const [exceptionsArray] = useState<string[]>(routeState.exceptionsArray);
  const [exceptionsPrimary] = useState<string>(routeState.exceptionsPrimary);
  const [fillableGapsPrimary] = useState<string>(routeState.fillableGapsPrimary);
  const [fillableGapsSecondary] = useState<string>(routeState.fillableGapsSecondary);
  const [gapsArray] = useState<string[]>(routeState.gapsArray);
  const [gapsPrimary] = useState<string>(routeState.gapsPrimary);
  const [unfillableGaps] = useState<string>(routeState.unfillableGaps);

  useEffect(() => {
    if (!!apiReadyFilters) {
      getAdequacyBreakDownDrillInData();
      getExceptionsDrillInData();
      getCompositeFillableGapsDrillInData();
    }
  }, [apiReadyFilters]);

  useEffect(() => {
    setPage(EcdReportGenerationWizardContextSteps.ExportDetails);
  }, []);

  useEffect(() => {
    setLoading(false);
    setError("");
  }, []);

  const getAdequacyBreakDownDrillInData = async () => {
    const data = await enterpriseComplianceDashboardService.getAdequacyBreakdownDrillInData(apiReadyFilters);
    setAdequacyBreakdownDrillInData(data);
  }

  const getExceptionsDrillInData = async () => {
    const data = await enterpriseComplianceDashboardService.getExceptionsDrillInData(apiReadyFilters);
    setExceptionsDrillInData(data);
  }

  const getCompositeFillableGapsDrillInData = async () => {
    const data = await enterpriseComplianceDashboardService.getCompositeFillableGapsDrillInData(apiReadyFilters);
    setcompositeFillableGapsDrillInData(data);
  }

  const getHtmlCanvasElement = async (selector: string, include: boolean) => {

    if (include) {
      const exportDiv = document.querySelector(selector) as HTMLElement;

      return html2canvas(exportDiv, {
        ignoreElements: function (element) {
          return element.classList.contains('ecd-preview-button')
        },
        onclone: (clonedDoc) => {
          const clonedExportDiv = clonedDoc.querySelector(selector) as HTMLElement;

          if (clonedExportDiv) {
            clonedExportDiv.className = 'ecd-export__preview-export-scale';
          }
        },
        scale: 2,
      });
    }

    return null;
  }

  const contextValue: EcdGenerateReportContextProps = {
    addGraphDetails,
    adequacyBreakdownData,
    adequacyBreakdownDrillInData,
    arrowNegative,
    arrowPositive,
    compositeFillableGapsData,
    compositeFillableGapsDrillInData,
    countyStatusData,
    defaultColors,
    displayFilters,
    error,
    exceptionsArray,
    exceptionsData,
    exceptionsDrillInData,
    exceptionsPrimary,
    exceptionsTimeFrame,
    fileName,
    fillableGapsPrimary,
    fillableGapsSecondary,
    gapOverviewData,
    gapOverviewTimeFrame,
    gapsArray,
    gapsByAgeData,
    gapsByAgeTimeFrame,
    gapsBySpecialtyData,
    gapsPrimary,
    getHtmlCanvasElement,
    includeAdequacyBreakdown,
    includeCompositeFillableGaps,
    includeCountyStatus,
    includeExceptions,
    includeFilterPage,
    includeGapOverview,
    includeGapsByAge,
    includeLineOfBusiness,
    includeTopCounties,
    includeTopSpecialties,
    lineOfBusinessData,
    loading,
    page,
    setAddGraphDetails,
    setFileName,
    setIncludeAdequacyBreakdown,
    setIncludeCompositeFillableGaps,
    setIncludeCountyStatus,
    setIncludeExceptions,
    setIncludeFilterPage,
    setIncludeGapOverview,
    setIncludeGapsByAge,
    setIncludeLineOfBusiness,
    setIncludeTopCounties,
    setIncludeTopSpecialties,
    setPage,
    topCountiesByGapData,
    unfillableGaps
  };

  return (
    <EcdGenerateReportContext.Provider value={contextValue}>
      {props.children}
    </EcdGenerateReportContext.Provider>
  );
}

export default EcdGenerateReportContextProvider;